import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Divider,
  Card,
  CardContent,
  TablePagination,
  Grid,
  Tooltip,
  InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MessageIcon from '@mui/icons-material/Message';
import SendIcon from '@mui/icons-material/Send';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { Config } from '../../../config/config';
import axios from 'axios';

// Interface für Customer-Daten
interface ICustomerComment {
  text: string;
  createdAt: string;
  author: string;
}

interface ICustomer {
  _id: string;
  name: string;
  company: string;
  position?: string;
  phone: string;
  email: string;
  address: string;
  washType?: string;
  message?: string;
  requestType: 'test' | 'contact';
  status: 'new' | 'contacted' | 'qualified' | 'proposal' | 'negotiation' | 'closed_won' | 'closed_lost' | 'angebot_vertrag' | 'nachfrage' | 'angenommen' | 'abgelehnt' | 'rechnung_bezahlt' | 'geonboarded';
  comments: ICustomerComment[];
  consentGiven: boolean;
  createdAt: string;
  updatedAt: string;
}

// Status-Mapping für Anzeige
const statusMapping = {
  new: { label: 'Anfrage', color: 'default' },
  contacted: { label: 'Kontaktiert', color: 'info' },
  qualified: { label: 'Qualifiziert', color: 'info' },
  proposal: { label: 'Angebot', color: 'warning' },
  negotiation: { label: 'Verhandlung', color: 'warning' },
  closed_won: { label: 'Gewonnen', color: 'success' },
  closed_lost: { label: 'Verloren', color: 'error' },
  angebot_vertrag: { label: 'Angebot & Vertrag verschickt', color: 'info' },
  nachfrage: { label: 'Nachfrage', color: 'warning' },
  angenommen: { label: 'Angenommen', color: 'success' },
  abgelehnt: { label: 'Abgelehnt', color: 'error' },
  rechnung_bezahlt: { label: 'Rechnung bezahlt', color: 'primary' },
  geonboarded: { label: 'Geonboarded', color: 'secondary' }
};

const CustomerManagementTab: React.FC = () => {
  // State für Kundenliste
  const [customers, setCustomers] = useState<ICustomer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // State für Filterung und Suche
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // State für Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  
  // State für ausgewählten Kunden und Dialog
  const [selectedCustomer, setSelectedCustomer] = useState<ICustomer | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  
  // State für Kommentar und Status-Update
  const [newComment, setNewComment] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);

  // Daten laden
  const fetchCustomers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const apiUrl = Config.apiRoot.endsWith('/api') 
        ? `${Config.apiRoot}/customer-info` 
        : `${Config.apiRoot}/api/customer-info`;
        
      const response = await axios.get(apiUrl, {
        params: {
          status: statusFilter !== 'all' ? statusFilter : undefined,
          searchTerm: searchTerm || undefined,
          page: page + 1,
          limit: rowsPerPage
        },
        withCredentials: true
      });
      
      if (response.data.success) {
        setCustomers(response.data.data);
        setTotalCount(response.data.pagination.total);
      } else {
        throw new Error(response.data.message || 'Fehler beim Laden der Kundendaten');
      }
    } catch (err) {
      console.error('Error fetching customers:', err);
      setError('Fehler beim Laden der Kundendaten. Bitte versuchen Sie es später erneut.');
    } finally {
      setLoading(false);
    }
  };

  // Beim ersten Rendern und bei Änderung der Filter Daten laden
  useEffect(() => {
    fetchCustomers();
  }, [statusFilter, page, rowsPerPage]);

  // Suche ausführen
  const handleSearch = () => {
    setPage(0);
    fetchCustomers();
  };
  
  // Suche bei Enter-Taste ausführen
  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Kundendaten ansehen
  const handleViewCustomer = async (customerId: string) => {
    try {
      setLoading(true);
      
      const apiUrl = Config.apiRoot.endsWith('/api') 
        ? `${Config.apiRoot}/customer-info/${customerId}` 
        : `${Config.apiRoot}/api/customer-info/${customerId}`;
        
      const response = await axios.get(apiUrl, {
        withCredentials: true
      });
      
      if (response.data.success) {
        setSelectedCustomer(response.data.data);
        setNewStatus(response.data.data.status);
        setDetailsDialogOpen(true);
      } else {
        throw new Error(response.data.message || 'Fehler beim Laden der Kundendetails');
      }
    } catch (err) {
      console.error('Error fetching customer details:', err);
      setError('Fehler beim Laden der Kundendetails. Bitte versuchen Sie es später erneut.');
    } finally {
      setLoading(false);
    }
  };

  // Kommentar hinzufügen
  const handleAddComment = async () => {
    if (!selectedCustomer || !newComment.trim()) return;
    
    try {
      setCommentLoading(true);
      
      const apiUrl = Config.apiRoot.endsWith('/api') 
        ? `${Config.apiRoot}/customer-info/${selectedCustomer._id}/comments` 
        : `${Config.apiRoot}/api/customer-info/${selectedCustomer._id}/comments`;
        
      const response = await axios.post(
        apiUrl, 
        {
          text: newComment,
          author: 'Admin' // Später durch tatsächlichen Benutzernamen ersetzen
        },
        { withCredentials: true }
      );
      
      if (response.data.success) {
        setSelectedCustomer(response.data.data);
        setNewComment('');
      } else {
        throw new Error(response.data.message || 'Fehler beim Hinzufügen des Kommentars');
      }
    } catch (err) {
      console.error('Error adding comment:', err);
      setError('Fehler beim Hinzufügen des Kommentars. Bitte versuchen Sie es später erneut.');
    } finally {
      setCommentLoading(false);
    }
  };

  // Status aktualisieren
  const handleUpdateStatus = async () => {
    if (!selectedCustomer || !newStatus || newStatus === selectedCustomer.status) return;
    
    try {
      setStatusLoading(true);
      console.log(`Aktualisiere Status: ID=${selectedCustomer._id}, Neuer Status=${newStatus}`);
      
      const apiUrl = Config.apiRoot.endsWith('/api') 
        ? `${Config.apiRoot}/customer-info/${selectedCustomer._id}/status` 
        : `${Config.apiRoot}/api/customer-info/${selectedCustomer._id}/status`;
        
      const response = await axios.patch(
        apiUrl, 
        {
          status: newStatus,
          username: 'Admin' // Später durch tatsächlichen Benutzernamen ersetzen
        },
        { withCredentials: true }
      );
      
      console.log('Status-Update Antwort:', response.data);
      
      if (response.data.success) {
        setSelectedCustomer(response.data.data);
        setError(null); // Zurücksetzen etwaiger vorheriger Fehler
        
        // Aktualisiere auch die Hauptliste
        fetchCustomers();
      } else {
        throw new Error(response.data.message || 'Fehler beim Aktualisieren des Status');
      }
    } catch (err: any) {
      console.error('Error updating status:', err);
      // Detaillierte Fehlerbehandlung
      let errorMessage = 'Fehler beim Aktualisieren des Status. Bitte versuchen Sie es später erneut.';
      
      if (err.response) {
        // Der Server hat mit einem Statuscode außerhalb des Bereichs 2xx geantwortet
        console.error('Server-Antwort:', err.response.data);
        errorMessage = err.response.data.message || err.response.data.error || errorMessage;
      } else if (err.request) {
        // Die Anfrage wurde gestellt, aber keine Antwort erhalten
        errorMessage = 'Keine Antwort vom Server erhalten.';
      } else {
        // Ein Fehler ist beim Einrichten der Anfrage aufgetreten
        errorMessage = err.message || errorMessage;
      }
      
      setError(errorMessage);
    } finally {
      setStatusLoading(false);
    }
  };

  // Dialog schließen
  const handleCloseDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setSelectedCustomer(null);
    setNewComment('');
  };

  // Formatiere Datum für die Anzeige
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Zeige Status als Chip an
  const renderStatusChip = (status: string) => {
    const statusInfo = statusMapping[status as keyof typeof statusMapping];
    // Fallback for unknown status values
    if (!statusInfo) {
      return <Chip label={status} color="default" size="small" />;
    }
    return (
      <Chip 
        label={statusInfo.label} 
        color={statusInfo.color as any}
        size="small"
      />
    );
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom component="div" sx={{ mb: 3 }}>
        Kundenanfragen
      </Typography>

      {/* Filter- und Suchleiste */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="status-filter-label">Status</InputLabel>
          <Select
            labelId="status-filter-label"
            id="status-filter"
            value={statusFilter}
            label="Status"
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="all">Alle Anfragen</MenuItem>
            <MenuItem value="new">Anfrage</MenuItem>
            <MenuItem value="angebot_vertrag">Angebot & Vertrag verschickt</MenuItem>
            <MenuItem value="nachfrage">Nachfrage</MenuItem>
            <MenuItem value="angenommen">Angenommen</MenuItem>
            <MenuItem value="abgelehnt">Abgelehnt</MenuItem>
            <MenuItem value="rechnung_bezahlt">Rechnung bezahlt</MenuItem>
            <MenuItem value="geonboarded">Geonboarded</MenuItem>
          </Select>
        </FormControl>
        
        <TextField
          label="Suche"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleSearch}>
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        
        <Box sx={{ flexGrow: 1 }} />
        
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchCustomers}
          size="small"
        >
          Aktualisieren
        </Button>
      </Box>

      {/* Fehlermeldung */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Tabelle mit Kunden */}
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Datum</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Firma</TableCell>
              <TableCell>Telefon</TableCell>
              <TableCell>E-Mail</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={40} />
                </TableCell>
              </TableRow>
            ) : customers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 2 }}>
                  Keine Kundenanfragen gefunden
                </TableCell>
              </TableRow>
            ) : (
              customers.map((customer) => (
                <TableRow 
                  key={customer._id} 
                  hover 
                  onClick={() => handleViewCustomer(customer._id)}
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    }
                  }}
                >
                  <TableCell>{formatDate(customer.createdAt)}</TableCell>
                  <TableCell>{customer.name}</TableCell>
                  <TableCell>{customer.company}</TableCell>
                  <TableCell>{customer.phone}</TableCell>
                  <TableCell>{customer.email}</TableCell>
                  <TableCell>{renderStatusChip(customer.status)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        labelRowsPerPage="Zeilen pro Seite:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} von ${count}`}
      />

      {/* Kundendetails-Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={handleCloseDetailsDialog}
        maxWidth="md"
        fullWidth
      >
        {selectedCustomer ? (
          <>
            <DialogTitle>
              Kundendetails: {selectedCustomer.name} ({selectedCustomer.company})
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={3}>
                {/* Linke Spalte: Kundendaten */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ mb: 3 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Kontaktinformationen
                      </Typography>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body1">
                          {selectedCustomer.name}
                          {selectedCustomer.position && ` (${selectedCustomer.position})`}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body1">{selectedCustomer.company}</Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body1">{selectedCustomer.phone}</Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body1">{selectedCustomer.email}</Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                        <LocationOnIcon sx={{ mr: 1, color: 'text.secondary', mt: 0.5 }} />
                        <Typography variant="body1">{selectedCustomer.address}</Typography>
                      </Box>
                    </CardContent>
                  </Card>
                  
                  {selectedCustomer.message && (
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Anfragenachricht
                        </Typography>
                        <Typography variant="body1">{selectedCustomer.message}</Typography>
                      </CardContent>
                    </Card>
                  )}
                </Grid>
                
                {/* Rechte Spalte: Status und Kommentare */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ mb: 3 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Status
                      </Typography>
                      
                      <Box sx={{ display: 'flex', mb: 2 }}>
                        <FormControl fullWidth size="small">
                          <InputLabel id="status-update-label">Status</InputLabel>
                          <Select
                            labelId="status-update-label"
                            value={newStatus}
                            label="Status"
                            onChange={(e) => setNewStatus(e.target.value)}
                            disabled={statusLoading}
                          >
                            <MenuItem value="new">Anfrage</MenuItem>
                            <MenuItem value="angebot_vertrag">Angebot & Vertrag verschickt</MenuItem>
                            <MenuItem value="nachfrage">Nachfrage</MenuItem>
                            <MenuItem value="angenommen">Angenommen</MenuItem>
                            <MenuItem value="abgelehnt">Abgelehnt</MenuItem>
                            <MenuItem value="rechnung_bezahlt">Rechnung bezahlt</MenuItem>
                            <MenuItem value="geonboarded">Geonboarded</MenuItem>
                          </Select>
                        </FormControl>
                        
                        <Button
                          variant="contained"
                          sx={{ ml: 1 }}
                          onClick={handleUpdateStatus}
                          disabled={statusLoading || newStatus === selectedCustomer.status}
                        >
                          {statusLoading ? <CircularProgress size={24} /> : 'Speichern'}
                        </Button>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ mr: 1 }}>
                          Aktueller Status:
                        </Typography>
                        {renderStatusChip(selectedCustomer.status)}
                      </Box>
                    </CardContent>
                  </Card>
                  
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Kommentare
                      </Typography>
                      
                      <Box sx={{ display: 'flex', mb: 2 }}>
                        <TextField
                          label="Neuer Kommentar"
                          multiline
                          rows={2}
                          fullWidth
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          disabled={commentLoading}
                        />
                        
                        <Button
                          variant="contained"
                          sx={{ ml: 1 }}
                          onClick={handleAddComment}
                          disabled={commentLoading || !newComment.trim()}
                        >
                          {commentLoading ? <CircularProgress size={24} /> : <SendIcon />}
                        </Button>
                      </Box>
                      
                      <Divider sx={{ mb: 2 }} />
                      
                      <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                        {selectedCustomer.comments && selectedCustomer.comments.length > 0 ? (
                          selectedCustomer.comments.slice().reverse().map((comment, index) => (
                            <Box key={index} sx={{ mb: 2, pb: 2, borderBottom: index !== selectedCustomer.comments.length - 1 ? '1px solid #eee' : 'none' }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                <Typography variant="subtitle2">{comment.author}</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <ScheduleIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
                                  <Typography variant="caption" color="text.secondary">
                                    {formatDate(comment.createdAt)}
                                  </Typography>
                                </Box>
                              </Box>
                              <Typography variant="body2">{comment.text}</Typography>
                            </Box>
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary" align="center">
                            Noch keine Kommentare vorhanden
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDetailsDialog}>Schließen</Button>
            </DialogActions>
          </>
        ) : (
          <DialogContent>
            <CircularProgress />
          </DialogContent>
        )}
      </Dialog>
    </Box>
  );
};

export default CustomerManagementTab; 